import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { Button, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DataObjectIcon from '@mui/icons-material/DataObject';
import Quill from 'quill';
import styles from '../../style/editor.module.css';
import { styled } from '@mui/material/styles';
import { getRandomInt } from '../../util/NumberUtil';
const Embed = Quill.import('blots/embed');
class TemplateMarker extends Embed {
    static create(value) {
        const node = super.create(value);
        node.setAttribute('class', styles.badge);
        node.innerHTML = value;
        return node;
    }
    static value(domNode) {
        return domNode.innerHTML;
    }
}
TemplateMarker.blotName = 'TemplateMarker';
TemplateMarker.tagName = 'span';
// https://stackoverflow.com/questions/69771687/quill-after-appendchild-is-not-a-function
TemplateMarker.className = 'template-class';
Quill.register({
    'formats/TemplateMarker': TemplateMarker,
});
const Block = Quill.import('blots/block');
Block.tagName = 'div';
Quill.register(Block);
const StyledMenu = styled((props) => _jsx(Menu, Object.assign({ elevation: 0 }, props)))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
    },
    '& .MuiMenuItem-root': {
        fontSize: '14px',
    },
}));
const insertMergeTag = (quill, mergeTag, overrideIndex) => {
    const selection = quill.getSelection(true);
    const index = overrideIndex ? overrideIndex : selection ? selection.index : 0;
    quill.insertEmbed(index, 'TemplateMarker', '{{' + mergeTag.value + '}}', 'user');
    if (!overrideIndex) {
        quill.insertText(index + 1, ' ', 'user');
        quill.setSelection(index + 1 + mergeTag.title.length, 0);
    }
};
export default function Editor(props) {
    let { isHtml, initialState, tags, onContentUpdate, showToolbar, placeholder } = props;
    const [content, setContent] = useState(initialState);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const quillRef = useRef(null);
    const templateMenuOpen = Boolean(anchorEl);
    useEffect(() => {
        var _a;
        let editor = (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.getEditor();
        if (editor) {
            editor.clipboard.dangerouslyPasteHTML(initialState);
            let i = 0;
            // MAX 30 Replacement, gotta use the i to shift things every time and embed is created
            while (editor.getText().indexOf('{{') != -1 && i <= 30) {
                let index1 = editor.getText().indexOf('{{');
                let index2 = editor.getText().indexOf('}}');
                let tag = editor.getText().substring(index1 + 2, index2);
                editor.deleteText(index1 + i, index2 - index1 + 2);
                insertMergeTag(editor, { title: tag, value: tag }, index1 + i);
                i++;
            }
        }
    }, [quillRef]);
    const toolbarId = useMemo(() => 'toolbar-' + getRandomInt(9999999), [isHtml]);
    const modules = {
        toolbar: showToolbar ? '#' + toolbarId : false,
    };
    const formats = isHtml
        ? ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'TemplateMarker']
        : ['TemplateMarker'];
    const handleTemplateMarkerButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleTemplateMarkerDropdownClose = () => {
        setAnchorEl(null);
    };
    const addTagButton = (_jsxs(_Fragment, { children: [_jsx(Button, { sx: {}, className: "ql-template", id: "template-marker-button", variant: "outlined", disableElevation: true, onClick: handleTemplateMarkerButtonClick, endIcon: _jsx(KeyboardArrowDownIcon, {}), children: _jsx(DataObjectIcon, {}) }), _jsx(StyledMenu, { id: "template-marker-menu", open: templateMenuOpen, onClose: handleTemplateMarkerDropdownClose, anchorEl: anchorEl, children: tags.map(tag => {
                    return (_jsx(MenuItem, { id: tag.value, onClick: () => {
                            var _a;
                            if (quillRef.current) {
                                insertMergeTag((_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.getEditor(), tag);
                            }
                            handleTemplateMarkerDropdownClose();
                        }, children: tag.title }));
                }) })] }));
    const toolBar = showToolbar && quillRef && (_jsxs("div", { id: toolbarId, children: [addTagButton, isHtml && _jsx("button", { className: "ql-bold", type: 'button' }), isHtml && _jsx("button", { className: "ql-italic", type: 'button' }), isHtml && _jsx("button", { className: "ql-underline", type: 'button' }), isHtml && _jsx("button", { className: "ql-strike", type: 'button' })] }));
    const editorComponent = (_jsx(ReactQuill, { theme: "snow", className: isHtml ? 'editor-html' : '', value: content, onChange: (test, test2, test3, test4) => {
            setContent(test);
            if (isHtml) {
                onContentUpdate(test);
            }
            else {
                onContentUpdate(test4.getHTML().replace(/<\/?[^>]+(>|$)/g, ''));
            }
        }, placeholder: placeholder || '', modules: modules, ref: quillRef, formats: formats, bounds: '.app' }));
    return (_jsx(_Fragment, { children: isHtml ? (_jsxs(_Fragment, { children: [' ', toolBar, " ", editorComponent] })) : (_jsxs("div", { style: { display: 'flex', alignItems: 'center' }, children: [showToolbar && _jsx("div", { style: { margin: 4, marginLeft: 0, width: '60px' }, children: toolBar }), _jsx("div", { style: { width: '100%' }, children: editorComponent })] })) }));
}
